/**
 * FAQ accordion.
 *
 * Toggles items open/closed with a smooth max-height animation. Supports
 * a "single open at a time" mode configurable per section via
 * `data-single-open="true"`. Fully keyboard-accessible; uses aria-expanded
 * and the `hidden` attribute for correct semantics.
 */

const SECTION_SELECTOR = '.js-faq';

class FaqAccordion {
    constructor(root) {
        this.root = root;
        this.singleOpen = root.dataset.singleOpen === 'true';
        this.toggles = Array.from(root.querySelectorAll('.js-faq-toggle'));

        this.onClick = this.onClick.bind(this);
        this.toggles.forEach((btn) => btn.addEventListener('click', this.onClick));
    }

    onClick(event) {
        const button = event.currentTarget;
        const item = button.closest('.faq-block__item');
        if (!item) return;

        const isOpen = item.classList.contains('is-open');

        if (this.singleOpen && !isOpen) {
            this.root
                .querySelectorAll('.faq-block__item.is-open')
                .forEach((openItem) => this.close(openItem));
        }

        if (isOpen) {
            this.close(item);
        } else {
            this.open(item);
        }
    }

    open(item) {
        const button = item.querySelector('.js-faq-toggle');
        const answer = item.querySelector('.faq-block__answer');
        if (!button || !answer) return;

        this.clearTransitionEnd(answer);

        item.classList.add('is-open');
        button.setAttribute('aria-expanded', 'true');
        answer.removeAttribute('hidden');

        // Animate to the natural height, then release to `none` so the panel
        // can grow if content changes (e.g., responsive images loading).
        answer.style.maxHeight = `${answer.scrollHeight}px`;

        const onEnd = (e) => {
            if (e.propertyName !== 'max-height') return;
            answer.style.maxHeight = 'none';
            this.clearTransitionEnd(answer);
        };
        answer._faqTransitionEnd = onEnd;
        answer.addEventListener('transitionend', onEnd);
    }

    close(item) {
        const button = item.querySelector('.js-faq-toggle');
        const answer = item.querySelector('.faq-block__answer');
        if (!button || !answer) return;

        this.clearTransitionEnd(answer);

        // Fix the current height so the transition has something to animate from.
        answer.style.maxHeight = `${answer.scrollHeight}px`;
        // Force reflow so the browser registers the fixed height before we
        // collapse to 0.
        // eslint-disable-next-line no-unused-expressions
        answer.offsetHeight;
        answer.style.maxHeight = '0px';

        item.classList.remove('is-open');
        button.setAttribute('aria-expanded', 'false');

        const onEnd = (e) => {
            if (e.propertyName !== 'max-height') return;
            answer.setAttribute('hidden', '');
            this.clearTransitionEnd(answer);
        };
        answer._faqTransitionEnd = onEnd;
        answer.addEventListener('transitionend', onEnd);
    }

    clearTransitionEnd(answer) {
        if (answer._faqTransitionEnd) {
            answer.removeEventListener('transitionend', answer._faqTransitionEnd);
            answer._faqTransitionEnd = null;
        }
    }
}

function init(scope = document) {
    scope.querySelectorAll(SECTION_SELECTOR).forEach((el) => {
        if (el.dataset.faqInit === 'true') return;
        el.dataset.faqInit = 'true';
        new FaqAccordion(el);
    });
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
} else {
    init();
}

// Re-init when Shopify re-renders the section in the theme editor.
document.addEventListener('shopify:section:load', (event) => {
    if (event.target) init(event.target);
});

